'use client'
import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import Image from 'next/image'
import { HeroHeader } from '@/components/header'

interface HeroProps {
  badge?: string
  title: string
  subtitle: string
  primaryCTA?: {
    label: string
    href: string
  }
  secondaryCTA?: {
    label: string
    href: string
  }
  media?: {
    type: 'image' | 'video'
    src: string
    alt: string
  }
  objectPosition?: string
}

export default function SevenLauxHero({
  badge,
  title,
  subtitle,
  primaryCTA,
  secondaryCTA,
  media,
  objectPosition = 'object-right'
}: HeroProps) {
  return (
    <>
      <HeroHeader />
      <main className="overflow-x-hidden">
        {/* Full-width 16:9 hero banner with background image */}
        <section className="relative w-full min-h-[34rem] lg:min-h-[44rem] lg:aspect-video overflow-hidden flex items-center pt-20 lg:pt-0">
          {/* Background Image */}
          {media && media.type === 'image' && (
            <div className="absolute inset-0 z-0">
              <Image
                className={`object-cover ${objectPosition}`}
                src={media.src}
                alt={media.alt}
                fill
                priority
                sizes="100vw"
                quality={90}
              />
              {/* Gradient overlays for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/60 via-neutral-900/40 to-neutral-800/20" />
              <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/30 via-transparent to-neutral-950/50" />
              {/* Bottom scrim: darkens the lower half so the title/CTAs stay legible over a busy image */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 lg:h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
              {/* Extra readability scrim behind the left-aligned text on mobile/tablet */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent lg:hidden" />
            </div>
          )}

          {/* Content Container */}
          <div className="relative z-10 w-full py-12 sm:py-14 md:py-20 lg:py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
              <div className="max-w-3xl">
                {badge && (
                  <span className="inline-block rounded-full bg-[url('/leather.jpg')] bg-cover bg-center px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-black mb-3 sm:mb-6 border border-[#2a1f10]/50 uppercase tracking-wider shadow-lg">
                    {badge}
                  </span>
                )}
                <h1 className="text-balance text-5xl sm:text-6xl font-medium font-[family-name:var(--font-bebas-neue)] md:text-7xl lg:text-8xl text-white drop-shadow-2xl">
                  {title}
                </h1>
                <p className="mt-3 sm:mt-6 text-pretty text-base sm:text-xl lg:text-2xl text-neutral-50 drop-shadow-lg max-w-2xl">
                  {subtitle}
                </p>

                <div className="mt-5 sm:mt-8 lg:mt-10 flex flex-col items-stretch gap-3 sm:gap-4 sm:flex-row sm:items-start">
                  {primaryCTA && (
                    <Button
                      asChild
                      size="lg"
                      className="w-full sm:w-auto justify-center px-6 py-4 sm:px-8 sm:py-6 text-base bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white border-2 border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:opacity-90 font-bold">
                      <Link href={primaryCTA.href}>
                        <span className="text-nowrap font-bold">{primaryCTA.label}</span>
                      </Link>
                    </Button>
                  )}
                  {secondaryCTA && (
                    <Button
                      asChild
                      size="lg"
                      className="w-full sm:w-auto justify-center px-6 py-4 sm:px-8 sm:py-6 text-base bg-white/10 hover:bg-white/20 text-white border-2 border-white/40 backdrop-blur-md shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-bold">
                      <Link href={secondaryCTA.href}>
                        <span className="text-nowrap font-bold">{secondaryCTA.label}</span>
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator (hidden — banner is 16:9, not full-screen) */}
          <div className="hidden">
            <div className="w-6 h-10 rounded-full border-2 border-neutral-50/60 flex items-start justify-center p-2">
              <div className="w-1 h-3 bg-neutral-50/60 rounded-full" />
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
